/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: partner-audio-mapping-rules-component.ts
 *
 * Created by kyang on 12/20/19.
 */

function PartnerAudioMappingRulesGridComponent(
    this: JS5Object,
    _: JS5Object,
    $q: JS5Object,
    uiGridConstants: JS5Object,
    LanguagesService: JS5Object,
    ModalService: JS5Object,
    PartnerEditService: JS5Object,
    RoleResourceFactory: JS5Object,
) {

    //======//
    // Grid //
    //======//

    this.gridColumnDefs = [
        {
            field: 'index',
            type: 'number',
            width: '70',
            displayName: 'Rule',
            enableCellEdit: false,
            cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
        },
        {
            field: 'layout1Pattern',
            minWidth: '155',
            displayName: 'Track Selector Layout Expression',
            cellTemplate: 'system/partner/editor/components/view/audioMappingRules/cellTemplates/validated-inline-edit-cell-template.html',
            isFieldValid: (layout: string) => layout && layout.length > 0,
        },
        {
            field: 'layout2Pattern',
            minWidth: '155',
            displayName: 'Combined Track Selector Layout Expression',
            cellTemplate: 'components/visual/grid/inline-edit-template.html'
        },
        {
            field: 'language',
            width: '230',
            displayName: 'Language',
            cellEditableCondition: false,
            cellTemplate: 'system/partner/editor/components/view/audioMappingRules/cellTemplates/language-cell-template.html',
            isFieldValid: (language: JS5Object) => !!language
        },
        {
            field: 'role',
            width: '230',
            displayName: 'Role',
            cellEditableCondition: false,
            cellTemplate: 'system/partner/editor/components/view/audioMappingRules/cellTemplates/role-cell-template.html',
            isFieldValid: (role: JS5Object) => !!role
        },
        {
            field: 'routingRatio',
            type: 'number',
            width: '160',
            displayName: 'Channel Routing Ratio',
            cellTemplate: 'system/partner/editor/components/view/audioMappingRules/cellTemplates/validated-inline-edit-cell-template.html',
            isFieldValid: (ratio: number) => Number.isInteger(ratio) && ratio >= 1 && ratio <= 32,
        },
    ];

    this.gridOptions = {
        rowSelection: true,
        enableRowHeaderSelection: false,
        enableSelectAll: false,
        enableColumnResizing: true,
        enablePaginationControls: false,
        enableSorting: false,
        enableColumnMenus: false,
        enableFullRowSelection: true,
        enableHorizontalScrollbar: uiGridConstants.ALWAYS,
        enableVerticalScrollbar: uiGridConstants.ALWAYS,
        multiSelect: false,
        rowHeight: 35,
        columnDefs: this.gridColumnDefs,
        rowTemplate: 'components/visual/grid/searchview-component-row-template.html',
        data: [],
        onRegisterApi: (gridApi: JS5Object) => {
            this.gridApi = gridApi;
            this.gridApi.edit.on.cancelCellEdit(null, this.checkColumnsValidity);
            this.gridApi.edit.on.afterCellEdit(null, this.checkColumnsValidity);
        }
    };

    this.checkColumnsValidity = () => {
        _.forEach(this.gridColumnDefs, (colDef: JS5Object) => {
            this.editorForm.$setValidity(
                `${colDef.field}Check`,
                !_.isFunction(colDef.isFieldValid) || _.every(
                    this.gridOptions.data,
                    (routingRule: JS5Object) => colDef.isFieldValid(routingRule[colDef.field])
                )
            );
        });
    };


    //===================//
    // Data Modification //
    //===================//

    const transformRoutingRulesForDisplay = () => {
        let languageAll = this.availableLanguages[0];
        let roleAll = this.availableRoles[0];
        _.forEach(this.partnerObject.audioRoutingRules, (routingRule: JS5Object, index: string) => {
            routingRule.index = parseInt(routingRule.index) + 1;
            routingRule.language = routingRule.language || languageAll;
            routingRule.role = routingRule.role || roleAll;
            routingRule.routingRatio = parseInt(routingRule.routingRatio);
            // Field is used by checkbox-cell to uniquely identify each row
            routingRule.name = index;
        });
    };

    const transformRoutingRulesForSaving = () => {
        _.forEach(this.gridOptions.data, (routingRule: JS5Object) => {
            routingRule.index = `${routingRule.index - 1}`;
            routingRule.routingRatio = `${routingRule.routingRatio}`;
            if (!routingRule.language.id) {
                delete routingRule.language;
            }
            if (!routingRule.role.id) {
                delete routingRule.role;
            }
            delete routingRule.name;
        });
    };

    const updateIndices = () => {
        _.forEach(this.gridOptions.data, (routingRule: JS5Object, index: string) => {
            routingRule.index = index + 1;
        })
    };


    //=========//
    // Toolbar //
    //=========//

    const canMoveRuleUp = () => {
        let selectedIndex = this.gridOptions.data.indexOf(this.gridApi.selection.getSelectedRows()[0]);
        return selectedIndex >= 1;
    };

    const canMoveRuleDown = () => {
        let selectedIndex = this.gridOptions.data.indexOf(this.gridApi.selection.getSelectedRows()[0]);
        return selectedIndex >= 0 && selectedIndex < this.gridOptions.data.length -1;
    };

    const onNewClicked = () => {
        this.gridOptions.data.push({
            index: this.gridOptions.data.length + 1,
            routingRatio: 1,
        });
        this.checkColumnsValidity();
        this.editorForm.$setDirty();
        this.gridApi.selection.clearSelectedRows();
    };

    const onSaveClicked = () => {
        transformRoutingRulesForSaving();
        this.loadingIndicator.show = true;
        PartnerEditService.saveEditData()
            .then(() => {
                this.resetTabData();
                this.loadingIndicator.show = false;
            });
    };

    const onDeleteClicked = () => {
        let audioMappingRule = this.gridApi.selection.getSelectedRows()[0];
        _.remove(this.gridOptions.data, audioMappingRule);
        updateIndices();
        this.checkColumnsValidity();
        this.editorForm.$setDirty();
        this.gridApi.selection.clearSelectedRows();
    };

    const onMoveUpClicked = () => {
        let selectedRule = this.gridApi.selection.getSelectedRows()[0];
        let selectedIndex = this.gridOptions.data.indexOf(selectedRule);
        let previousRule = this.gridOptions.data[selectedIndex -1];
        this.gridOptions.data.splice(
            selectedIndex -1, 2,
            selectedRule,
            previousRule);
        updateIndices();
        this.editorForm.$setDirty();
    };

    const onMoveDownClicked = () => {
        let selectedRule = this.gridApi.selection.getSelectedRows()[0];
        let selectedIndex = this.gridOptions.data.indexOf(selectedRule);
        let nextRule = this.gridOptions.data[selectedIndex +1];
        this.gridOptions.data.splice(
            selectedIndex, 2,
            nextRule,
            selectedRule);
        updateIndices();
        this.editorForm.$setDirty();
    };

    this.toolbarButtonDefs = [{
            label: 'New',
            enabled: () => true,
            onClick: onNewClicked,
        }, {
            label: 'Save',
            enabled: () => this.editorForm.$dirty && this.editorForm.$valid,
            onClick: onSaveClicked,
        }, {
            label: 'Revert',
            enabled: () => this.editorForm.$dirty,
            onClick: () => PartnerEditService.revertEditData(),
        }, {
            label: 'Delete',
            enabled: () => this.gridApi.selection.getSelectedCount() >= 1,
            onClick: onDeleteClicked,
        }, {
            label: 'Move Up',
            icon: 'fa-arrow-up',
            enabled: canMoveRuleUp,
            onClick: onMoveUpClicked,
        }, {
            label: 'Move Down',
            icon: 'fa-arrow-down',
            enabled: canMoveRuleDown,
            onClick: onMoveDownClicked,
        }];


    //=================//
    // Lifecycle Hooks //
    //=================//

    this.resetTabData = () => {
        this.partnerObject = PartnerEditService.partnerObject;
        transformRoutingRulesForDisplay();
        this.partnerObject.audioRoutingRules = _.sortBy(this.partnerObject.audioRoutingRules, 'index');
        this.gridOptions.data = this.partnerObject.audioRoutingRules;
        if (this.gridApi) {
            this.gridApi.selection.clearSelectedRows();
        }
        this.checkColumnsValidity();
    };

    this.$onInit = () => {
        this.loadingIndicator = {
            backdrop: true,
            show: false,
            title: 'Working',
            message: 'Please wait...'
        };

        let languagesPromise = LanguagesService.getAllLanguages()
            .then((availableLanguages: JS5Object[]) => {
                this.availableLanguages = _.orderBy(availableLanguages, 'name');
                this.availableLanguages.unshift({
                    name: '[All]',
                    id: null
                });
            });
        let rolesPromise = RoleResourceFactory.query({mediaType: 'Audio'})
            .$promise.then((availableRoles: JS5Object[]) => {
                this.availableRoles = _.orderBy(availableRoles, 'name');
                this.availableRoles.unshift({
                    name: '[All]',
                    id: null
                });
            });

        this.loadingIndicator.show = true;
        $q.all([
            languagesPromise,
            rolesPromise,
        ]).then(() => {
            this.resetTabData();
            this.loadingIndicator.show = false;
        });
    };

    this.$postLink = () => {
        PartnerEditService.editorForm = this.editorForm;
        PartnerEditService.tabRevertFunction = this.resetTabData;
    };

    this.$onDestroy = () => {
        PartnerEditService.editorForm = null;
        PartnerEditService.tabRevertFunction = null;
    };

}

angular.module('prism-ui.partner').component(
    'partnerAudioMappingRulesGrid',
    {
        controller: PartnerAudioMappingRulesGridComponent,
        templateUrl: 'system/partner/editor/components/view/audioMappingRules/partner-audio-mapping-rules-grid-template.html',
        bindings: {
            canRead: '<',
            canWrite: '<',
            partnerObject: '<'
        }
    });
